/**
 * 목록 날짜 칸의 보조 줄 — 목업 `.date .r`(`오늘` · `5일 전` · `19일 전`).
 * 문서관리 목록과 현장 목록이 **같은 시안 요소**를 그리므로 두 곳이 이 하나를 쓴다.
 *
 * 공용 `@safetyhub/shared`의 `formatRelativeTime`을 쓰지 않는 이유:
 * - 7일이 넘으면 **날짜 문자열**로 떨어지는데, 여기서는 바로 윗줄(`.date .d`)이 이미 날짜라
 *   같은 값이 두 번 찍힌다. 이 자리에 필요한 건 시각이 아니라 "며칠 전인가"뿐이다.
 * - 경계를 **경과 밀리초**로 나눠서, 어젯밤 23시가 "0일 전"(=오늘)이 되어 사용자가 읽는
 *   날짜와 어긋난다. 여기는 **달력 날짜 기준**으로 센다.
 *
 * ⚠️ 2026-07-28에 두 워커가 같은 시안 요소에서 이 한계에 각자 부딪혀 **서로 다르게** 풀었다
 * (한쪽은 7일 넘으면 보조 줄을 숨김 / 한쪽은 이 함수를 신설). 같은 요소가 화면마다 다르게
 * 동작하던 것을 이 파일로 합쳤다 — **relative 표시를 새로 만들기 전에 여기부터 볼 것.**
 */

/** 로컬 자정 기준 일수 차이(양수 = 과거). */
function dayDistance(target: Date, now: Date): number {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/**
 * 날짜를 "오늘 / 어제 / N일 전 / N개월 전 / N년 전"으로 옮긴다.
 * 미래 날짜(예약일 등)는 "내일 / N일 후"로 대칭 처리한다.
 * 유효하지 않은 날짜면 `null` — 호출부가 보조 줄 자체를 그리지 않는다.
 */
export function formatDayDistance(value: string | Date, now: Date = new Date()): string | null {
  const target = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(target.getTime())) return null;

  const days = dayDistance(target, now);
  if (days === 0) return '오늘';
  if (days === 1) return '어제';
  if (days === -1) return '내일';
  if (days < 0) return `${-days}일 후`;
  if (days < 30) return `${days}일 전`;
  if (days < 365) return `${Math.floor(days / 30)}개월 전`;
  return `${Math.floor(days / 365)}년 전`;
}
